import { supabase } from './supabaseClient';
import { SPEC_TEXT_FIELDS, Watch, WatchPatch, watchesApi } from './watchesApi';

/**
 * Client side of the `scrape-watch` Edge Function.
 *
 * Kept out of watchesApi for the same reason watchesApi is kept out of the
 * components: different backend, different concerns. This one calls a function
 * and returns a proposal; watchesApi is the only thing that writes rows.
 */

export interface ScrapedFields {
  case_diameter?: string;
  case_material?: string;
  movement?: string;
  power_reserve?: string;
  water_resistance?: string;
  crystal?: string;
  strap?: string;
  price_msrp?: string;
  year?: number;
}

export interface ScrapeResult {
  /** The page's own title, shown as context. Never written to the row. */
  title: string | null;
  fields: ScrapedFields;
  /** Storage path of the downloaded image, when one was fetched and stored. */
  imagePath: string | null;
  /** The URL actually read, after redirects. */
  sourceUrl: string;
  /** Things that went wrong but did not stop the scrape (usually the image). */
  warnings: string[];
}

/** "www.tudorwatch.com/x/y" -> "tudorwatch.com", for links and status lines. */
export const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export const scrapeApi = {
  fromUrl: async (opts: {
    url: string;
    watchId: string;
    wantImage: boolean;
  }): Promise<ScrapeResult> => {
    const { data, error } = await supabase.functions.invoke<ScrapeResult>('scrape-watch', {
      body: opts,
    });

    if (error) {
      // A non-2xx reply arrives as a FunctionsHttpError carrying the raw
      // Response. The function puts a sentence meant for a person in
      // { error }, so prefer that over the generic "non-2xx status code".
      let message = error.message;
      const context = (error as { context?: Response }).context;
      if (context && typeof context.json === 'function') {
        try {
          const body = await context.json();
          if (body?.error) message = String(body.error);
        } catch {
          // Body was not JSON; the generic message is what we have.
        }
      }
      throw new Error(message);
    }

    if (!data) throw new Error('The page could not be read.');
    return data;
  },
};

export interface ScrapeApplication {
  patch: WatchPatch;
  /** Labels of the columns that were actually filled, for the status line. */
  filled: string[];
  imageApplied: boolean;
}

/**
 * Work out what a scrape should change on a watch.
 *
 * Fill-blanks only: a field that already has a value is never overwritten,
 * which is the same promise "Fill from reference data" makes and the reason a
 * scrape is safe to re-run. Kept pure and separate from the component so the
 * rule is testable on its own.
 */
export function applyScrape(watch: Watch, result: ScrapeResult): ScrapeApplication {
  const patch: WatchPatch = {};
  const filled: string[] = [];

  for (const key of SPEC_TEXT_FIELDS) {
    const found = result.fields[key];
    if (found && !watch[key]) {
      patch[key] = found;
      filled.push(key);
    }
  }

  if (result.fields.year != null && watch.year == null) {
    patch.year = result.fields.year;
    filled.push('year');
  }

  // The owner's own photo always wins; the function is only asked for an image
  // when there isn't one, but check again in case it changed in between.
  const imageApplied = Boolean(result.imagePath && !watch.image_url);
  if (imageApplied) patch.image_url = result.imagePath;

  if (filled.length > 0) {
    patch.specs_updated_at = new Date().toISOString();
    // 'manual' outranks a scrape: once the owner has been through the specs by
    // hand the row is theirs, even if a later scrape fills a gap they left.
    if (watch.specs_source !== 'manual') patch.specs_source = 'scraped';
  }

  return { patch, filled, imageApplied };
}

export interface ScrapeOutcome {
  /** The watch after the fill-in was written, or unchanged if nothing was. */
  watch: Watch;
  /** One line describing what happened, ready to show. */
  note: string;
  warnings: string[];
}

/**
 * Read a watch's source page and write in whatever was missing.
 *
 * The whole operation, in one place, because two screens start it: saving a
 * URL on the detail page, and adding a watch with one. Duplicating the
 * "fetch, apply, update, describe" sequence across both is how they would
 * quietly come to disagree about what a scrape does.
 *
 * Throws if the page cannot be read; callers show that as an error. A page
 * that reads fine but yields nothing is not a failure -- it returns a note
 * saying so, because that is a real and common outcome worth reporting
 * plainly rather than dressing up as a problem.
 */
export async function scrapeInto(watch: Watch): Promise<ScrapeOutcome> {
  if (!watch.source_url) return { watch, note: '', warnings: [] };

  const result = await scrapeApi.fromUrl({
    url: watch.source_url,
    watchId: watch.id,
    // Only ask for a picture when there isn't one: an owner's photo of their
    // actual watch beats a press shot of the model.
    wantImage: !watch.image_url,
  });

  const { patch, filled, imageApplied } = applyScrape(watch, result);
  const updated =
    Object.keys(patch).length > 0 ? await watchesApi.update(watch.id, patch) : watch;

  const parts: string[] = [];
  if (filled.length > 0) {
    parts.push(`filled ${filled.length} ${filled.length === 1 ? 'field' : 'fields'}`);
  }
  if (imageApplied) parts.push('added a photo');

  const host = hostOf(result.sourceUrl);
  return {
    watch: updated,
    note:
      parts.length > 0
        ? `Read ${host} — ${parts.join(' and ')}.`
        : `Read ${host} — nothing new to fill in.`,
    warnings: result.warnings ?? [],
  };
}
